import {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import { ApiDestination } from '../llm-common/llm-types';
import {
  displayNameToOptionWithDeleted,
  variableToOptionDeleted,
} from 'services/helpers';
import {
  checkDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import {
  DEFAULT_CONFLICT_RESOLUTION,
  DESTINATION_TYPES,
} from '../llm-common/constants';
import { AudioTranscriptionStep } from '../actionSectionTypes';
import type {
  ValueSource,
  ValueSourceFromAPI,
} from 'pages/AutomationEngine/types';
import { AudioTranscriptionContextValue } from './types';
import { VALUE_SOURCE_TYPES } from '__pages/AutomationEngine/constants';

type ApiObject = {
  inputValueSource: ValueSourceFromAPI;
  modelName: string;
  businessPluginApp: any;
  prompt: string;
  htmlPrompt: string;
  mergeFieldValidation: any;
  defaultToValue: any;
  destinations: ApiDestination[];
  isAdvanced: boolean;
};

const forFlow = (
  apiObject: ApiObject,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary,
  stepType: any
): Omit<AudioTranscriptionContextValue, 'model' | 'errors'> => {
  const {
    inputValueSource,
    modelName,
    businessPluginApp,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations,
    isAdvanced,
  } = apiObject;

  switch (inputValueSource?.type) {
    case 'context_entity_field':
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.field,
        displayNameDescriptor
      );
      break;
    case 'related_object_field':
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.relationshipField,
        displayNameDescriptor
      );
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.field,
        displayNameDescriptor
      );
      break;
    case 'automation_entity_variable_field': {
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.field,
        displayNameDescriptor
      );
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.variable,
        nameDescriptor
      );
      break;
    }
    case 'automation_variable':
      checkDeleted(
        messageDictionary,
        addErrorMessage,
        inputValueSource?.variable,
        nameDescriptor
      );
      break;
  }

  if (!modelName || !businessPluginApp) {
    addErrorMessage(messageDictionary.llmPluginAppIsMissing);
  }

  return {
    inputValueSource: {
      type: inputValueSource?.type || VALUE_SOURCE_TYPES.CONTEXT_ENTITY_FIELD,
      field: inputValueSource?.field
        ? displayNameToOptionWithDeleted(
            inputValueSource.field,
            messageDictionary.deleted
          )
        : null,
      relationshipField: inputValueSource?.relationshipField
        ? displayNameToOptionWithDeleted(
            inputValueSource.relationshipField,
            messageDictionary.deleted
          )
        : null,
      variable: inputValueSource?.variable
        ? variableToOptionDeleted(inputValueSource.variable)
        : null,
      value: null,
      valueCustomObject: null,
    } as ValueSource,
    modelName: modelName || null,
    businessPluginAppId: businessPluginApp?.id || null,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations: destinations.map(
      ({
        relatedObjectField,
        field,
        variable,
        conflictResolution,
        isRequired,
        confidenceThreshold,
        options,
      }) => {
        checkDeleted(
          messageDictionary,
          addErrorMessage,
          relatedObjectField,
          displayNameDescriptor
        );

        checkDeleted(
          messageDictionary,
          addErrorMessage,
          field,
          displayNameDescriptor
        );

        return {
          field: field
            ? displayNameToOptionWithDeleted(field, messageDictionary.deleted)
            : null,
          relatedObjectField:
            !field?.deleted && relatedObjectField // if field is deleted always return null
              ? displayNameToOptionWithDeleted(
                  relatedObjectField,
                  messageDictionary.deleted
                )
              : null,
          variable: variable
            ? displayNameToOptionWithDeleted(
                { ...variable, displayName: undefined },
                messageDictionary.deleted
              )
            : null,
          conflictResolution: conflictResolution || DEFAULT_CONFLICT_RESOLUTION,
          isRequired: isRequired || false,
          options: (options || []).map((option) =>
            displayNameToOptionWithDeleted(option, messageDictionary.deleted)
          ),
          confidenceThreshold,
          destinationType: relatedObjectField
            ? DESTINATION_TYPES.related_object_field
            : variable
              ? DESTINATION_TYPES.variable
              : DESTINATION_TYPES.this_object_field,
        };
      }
    ),
    isAdvanced,
  };
};

const forApi = (
  step: AudioTranscriptionStep,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const {
    inputValueSource,
    modelName,
    businessPluginAppId,
    prompt,
    htmlPrompt,
    mergeFieldValidation,
    destinations,
    isAdvanced,
  } = step.details;

  const api = {
    ...step,
    details: {
      inputValueSource: {
        type: inputValueSource?.type || null,
        field: inputValueSource?.field?.value || null,
        relationshipField: inputValueSource?.relationshipField?.value || null,
        variable: inputValueSource?.variable?.value || null,
      },
      modelName,
      businessPluginAppId,
      prompt,
      htmlPrompt,
      mergeFieldValidation,
      defaultToValue: 'unknown',
      destinations: destinations.map((destination) => {
        const {
          relatedObjectField,
          field,
          variable,
          conflictResolution,
          options = [],
          isRequired,
          confidenceThreshold,
        } = destination;
        return {
          relatedObjectField: relatedObjectField?.value,
          field: field?.value,
          variable: variable?.label,
          conflictResolution: conflictResolution || DEFAULT_CONFLICT_RESOLUTION,
          options: options.map((option) => option.value),
          isRequired,
          confidenceThreshold,
        };
      }),
      isAdvanced,
    },
  };

  return api;
};

export const audioTranscriptionDTO = {
  forApi,
  forFlow,
};
